import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import PhoneIcon from '@mui/icons-material/Phone';
import GmbCreationForm from '../components/Payment/GmbCreationForm';
import AddPhoneToGmbForm from '../components/Payment/AddPhoneToGmbForm';
import DashboardLayout from '../components/Layouts/DashboardLayout';

const PageContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(4),
    backgroundColor: '#f8f8f8',
    minHeight: '100vh'
}));

const PageTitle = styled(Typography)(({ theme }) => ({
    fontFamily: 'Inter, sans-serif',
    fontSize: '28px',
    fontWeight: 700,
    color: '#0B91D6',
    marginBottom: theme.spacing(1)
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    color: '#30302E',
    marginBottom: theme.spacing(4)
}));

const RequestCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    border: '1px solid #E0E0E0',
    backgroundColor: '#ffffff',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
        boxShadow: '0 8px 24px rgba(11, 145, 214, 0.15)',
        borderColor: '#0B91D6',
        transform: 'translateY(-4px)'
    }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
    width: 80,
    height: 80,
    borderRadius: '50%',
    backgroundColor: 'rgba(11, 145, 214, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2)
}));

const CardTitle = styled(Typography)(({ theme }) => ({
    fontFamily: 'Inter, sans-serif',
    fontSize: '20px',
    fontWeight: 600,
    color: '#121927',
    marginBottom: theme.spacing(1)
}));

const CardDescription = styled(Typography)(({ theme }) => ({
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    color: '#666666',
    marginBottom: theme.spacing(2),
    lineHeight: 1.6
}));

const PriceTag = styled(Typography)(({ theme }) => ({
    fontFamily: 'Inter, sans-serif',
    fontSize: '24px',
    fontWeight: 700,
    color: '#0B91D6',
    marginBottom: theme.spacing(2)
}));

const RequestsPage = () => {
    const [createProfileOpen, setCreateProfileOpen] = useState(false);
    const [addPhoneOpen, setAddPhoneOpen] = useState(false);

    return (
        <DashboardLayout>
            <PageContainer>
                <PageTitle>Requests</PageTitle>
                <PageSubtitle>
                    Submit requests for GMB profile creation or updates
                </PageSubtitle>

                <Grid container spacing={4} sx={{ maxWidth: 900 }}>
                    {/* Create New GMB Profile */}
                    <Grid item xs={12} md={6}>
                        <RequestCard onClick={() => setCreateProfileOpen(true)}>
                            <IconWrapper>
                                <AddBusinessIcon sx={{ fontSize: 40, color: '#0B91D6' }} />
                            </IconWrapper>
                            <CardTitle>Create New GMB Profile</CardTitle>
                            <CardDescription>
                                Don't have a Google My Business profile yet?
                                We'll create one for your business with complete setup.
                            </CardDescription>
                            <PriceTag>₹10,000</PriceTag>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                                One-time payment
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                fullWidth
                                sx={{ mt: 'auto' }}
                            >
                                Get Started
                            </Button>
                        </RequestCard>
                    </Grid>

                    {/* Add Phone to GMB */}
                    <Grid item xs={12} md={6}>
                        <RequestCard onClick={() => setAddPhoneOpen(true)}>
                            <IconWrapper>
                                <PhoneIcon sx={{ fontSize: 40, color: '#0B91D6' }} />
                            </IconWrapper>
                            <CardTitle>Add Number to GMB</CardTitle>
                            <CardDescription>
                                Need to add or update the phone number on your
                                existing GMB profile? Submit a request and we'll help.
                            </CardDescription>
                            <PriceTag>Free</PriceTag>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                                Request verification required
                            </Typography>
                            <Button
                                variant="outlined"
                                size="large"
                                fullWidth
                                sx={{ mt: 'auto' }}
                            >
                                Submit Request
                            </Button>
                        </RequestCard>
                    </Grid>
                </Grid>

                {/* GMB Creation Form Dialog */}
                <GmbCreationForm
                    open={createProfileOpen}
                    onClose={() => setCreateProfileOpen(false)}
                />

                {/* Add Phone Form Dialog */}
                <AddPhoneToGmbForm
                    open={addPhoneOpen}
                    onClose={() => setAddPhoneOpen(false)}
                />
            </PageContainer>
        </DashboardLayout>
    );
};

export default RequestsPage;
